<script setup>
import { useFileUpload } from '@/composables/useFileUpload'
import { useApi } from '@/utils/api'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Product',
  },
})

const router = useRouter()

// ── State ──────────────────────────────────────────────────────────────────
const loading = ref(false)
const errors = ref({})
const imageFile = ref(null)
const imagePreview = ref(null)
const snackbar = ref({ show: false, text: '', color: 'success' })
const categories = ref([])
const loadingCategories = ref(false)
const { uploading: uploadingImage, upload } = useFileUpload()

const form = ref({
  name: '',
  category_id: null,
  description: '',
  is_active: true,
  sort_order: 0,
  variants: [
    { name: 'Regular', sku: '', barcode: '', price: 0, is_active: true },
  ],
})

// ── Categories ─────────────────────────────────────────────────────────────
const fetchCategories = async () => {
  loadingCategories.value = true
  try {
    // ✅ useApi().get() return response langsung
    // ✅ Response parsing: response?.data ?? response (Addendum 5A.4)
    const response = await useApi('/api/v1/categories', { query: { per_page: 100 } }).get()
    const data = response?.data ?? response
    
    categories.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error('Failed to load categories:', e)
    showSnackbar('Gagal memuat kategori', 'error')
  } finally {
    loadingCategories.value = false
  }
}
onMounted(fetchCategories)

// ── Variants ───────────────────────────────────────────────────────────────
const addVariant = () => {
  form.value.variants.push({ name: '', sku: '', barcode: '', price: 0, is_active: true })
}

const removeVariant = index => {
  if (form.value.variants.length <= 1) return
  form.value.variants.splice(index, 1)
}

// ── Image ──────────────────────────────────────────────────────────────────
const handleImageChange = file => {
  if (!file) { 
    imagePreview.value = null
    imageFile.value = null
    return 
  }
  imageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

// ── Submit ─────────────────────────────────────────────────────────────────
const submit = async () => {
  loading.value = true
  errors.value = {}
  
  try {
    if (!form.value.variants || form.value.variants.length === 0) {
      errors.value.variants = ['Produk harus memiliki minimal 1 varian.']
      showSnackbar('Produk harus memiliki minimal 1 varian', 'warning')
      loading.value = false
      return
    }
    
    for (let i = 0; i < form.value.variants.length; i++) {
      const v = form.value.variants[i]
      if (!v.name || v.name.trim() === '') {
        errors.value[`variants.${i}.name`] = ['Nama varian wajib diisi']
        showSnackbar('Nama varian wajib diisi', 'warning')
        loading.value = false
        return
      }
      if (!v.price || v.price <= 0) {
        errors.value[`variants.${i}.price`] = ['Harga harus lebih dari 0']
        showSnackbar('Harga harus lebih dari 0', 'warning')
        loading.value = false
        return
      }
    }
    
    // Prepare payload
    const payload = {
      name: form.value.name?.trim(),
      category_id: form.value.category_id,
      description: form.value.description?.trim() || null,
      is_active: form.value.is_active,
      sort_order: form.value.sort_order || 0,
      variants: form.value.variants.map(v => ({
        name: v.name?.trim(),
        sku: v.sku?.trim() || null,
        barcode: v.barcode?.trim() || null,
        price: parseFloat(v.price) || 0,
        is_active: v.is_active !== undefined ? v.is_active : true,
      })),
    }
    
    const response = await useApi('/api/v1/products').post({ body: payload })
    const data = response?.data ?? response
    const productId = data?.id ?? data?.data?.id
    
    if (imageFile.value && productId) {
      const { error: uploadError } = await upload(
        imageFile.value,
        `/api/v1/products/${productId}/image`,
        'image'
      )
      
      if (uploadError) {
        console.warn('Image upload failed:', uploadError)
        showSnackbar('Produk berhasil dibuat, tapi upload gambar gagal', 'warning')
      } else {
        showSnackbar('Produk berhasil dibuat')
      }
    } else {
      showSnackbar('Produk berhasil dibuat')
    }
    
    // Redirect ke list products
    router.push({ name: 'catalog-products' })
    
  } catch (err) {
    console.error('Submit error:', err)
    console.error('Error data:', err?.data)
    
    if (err?.status === 422 || err?.response?.status === 422) {
      errors.value = err?.data?.errors ?? err?.response?.data?.errors ?? {}
      showSnackbar(err?.data?.message ?? 'Periksa kembali isian form', 'warning')
    } else {
      showSnackbar(err?.data?.message ?? err?.response?.data?.message ?? 'Terjadi kesalahan', 'error')
    }
  } finally {
    loading.value = false
  }
}

const showSnackbar = (text, color = 'success') => {
  snackbar.value = { show: true, text, color }
}

const breadcrumbs = [
  { title: 'Katalog', disabled: true },
  { title: 'Produk', to: { name: 'catalog-products' } },
  { title: 'Tambah Produk', disabled: true },
]
</script>

<template>
  <div>
    <VBreadcrumbs :items="breadcrumbs" class="px-0 mb-4" />
    
    <VRow>
      <!-- Left: Main form -->
      <VCol cols="12" md="8">
        <!-- Basic info -->
        <VCard class="mb-4">
          <VCardTitle class="pa-5 pb-0">Informasi Produk</VCardTitle>
          <VCardText class="pt-4">
            <VRow dense>
              <VCol cols="12">
                <VTextField
                  v-model="form.name"
                  label="Nama Produk *"
                  placeholder="contoh: Kopi Susu"
                  :error-messages="errors['name']"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VSelect
                  v-model="form.category_id"
                  :items="categories"
                  item-title="name"
                  item-value="id"
                  label="Kategori"
                  clearable
                  :loading="loadingCategories"
                  :error-messages="errors['category_id']"
                />
              </VCol>
              <VCol cols="12" sm="6">
                <VTextField
                  v-model.number="form.sort_order"
                  label="Urutan Tampil"
                  type="number"
                  min="0"
                  :error-messages="errors['sort_order']"
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="form.description"
                  label="Deskripsi"
                  rows="3"
                  :error-messages="errors['description']"
                />
              </VCol>
            </VRow>
          </VCardText>
        </VCard>

        <!-- Variants -->
        <VCard>
          <VCardTitle class="pa-5 pb-0 d-flex align-center justify-space-between">
            <span>Varian &amp; Harga</span>
            <VBtn
              size="small"
              variant="tonal"
              prepend-icon="ri-add-line"
              @click="addVariant"
            >
              Tambah Varian
            </VBtn>
          </VCardTitle>
          <VCardText class="pt-4">
            <VAlert
              v-if="errors['variants']"
              type="error"
              variant="tonal"
              class="mb-4"
            >
              {{ errors['variants'][0] }}
            </VAlert>

            <div
              v-for="(variant, idx) in form.variants"
              :key="idx"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-body-2 font-weight-medium text-medium-emphasis">
                  Varian {{ idx + 1 }}
                </span>
                <VBtn
                  v-if="form.variants.length > 1"
                  icon
                  variant="text"
                  size="x-small"
                  color="error"
                  @click="removeVariant(idx)"
                >
                  <VIcon icon="ri-delete-bin-line" />
                </VBtn>
              </div>

              <VRow dense>
                <VCol cols="12" sm="5">
                  <VTextField
                    v-model="variant.name"
                    label="Nama Varian *"
                    placeholder="Regular / Large"
                    :error-messages="errors[`variants.${idx}.name`]"
                  />
                </VCol>
                <VCol cols="6" sm="3">
                  <VTextField
                    v-model="variant.sku"
                    label="SKU"
                    :error-messages="errors[`variants.${idx}.sku`]"
                  />
                </VCol>
                <VCol cols="6" sm="4">
                  <VTextField
                    v-model.number="variant.price"
                    label="Harga *"
                    type="number"
                    min="0"
                    prefix="Rp "
                    :error-messages="errors[`variants.${idx}.price`]"
                  />
                </VCol>
                <VCol cols="12" sm="6">
                  <VTextField
                    v-model="variant.barcode"
                    label="Barcode (opsional)"
                    placeholder="Scan atau ketik manual"
                    prepend-inner-icon="ri-barcode-line"
                    :error-messages="errors[`variants.${idx}.barcode`]"
                    hint="Dipakai untuk scan cepat di POS (fitur Retail)"
                    persistent-hint
                  />
                </VCol>
              </VRow>

              <VDivider v-if="idx < form.variants.length - 1" class="my-3" />
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Right: image + status -->
      <VCol cols="12" md="4">
        <VCard class="mb-4">
          <VCardTitle class="pa-5 pb-0">Foto Produk</VCardTitle>
          <VCardText class="pt-4">
            <div
              class="image-upload-area rounded-lg d-flex flex-column align-center justify-center pa-4 mb-3"
              :class="imagePreview ? '' : 'border-dashed'"
            >
              <VImg
                v-if="imagePreview"
                :src="imagePreview"
                height="160"
                cover
                rounded="lg"
              />
              <div v-else class="text-center">
                <VIcon icon="ri-image-add-line" size="40" color="medium-emphasis" />
                <p class="text-caption text-medium-emphasis mt-2 mb-0">Klik untuk unggah foto</p>
              </div>
            </div>
            <VFileInput
              label="Pilih Foto"
              accept="image/*"
              hide-details
              prepend-icon=""
              prepend-inner-icon="ri-upload-line"
              variant="outlined"
              :loading="uploadingImage"
              @update:model-value="handleImageChange"
            />
            <p class="text-caption text-medium-emphasis mt-1">Maks. 2MB · JPG, PNG</p>
          </VCardText>
        </VCard>

        <VCard>
          <VCardTitle class="pa-5 pb-0">Status</VCardTitle>
          <VCardText class="pt-4">
            <div class="d-flex align-center justify-space-between">
              <span class="text-body-2">Produk Aktif</span>
              <VSwitch v-model="form.is_active" hide-details color="success" />
            </div>
            <p class="text-caption text-medium-emphasis mt-1">
              Produk non-aktif tidak muncul di POS
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Footer actions -->
    <VRow class="mt-2">
      <VCol cols="12" class="d-flex gap-3 justify-end">
        <VBtn variant="tonal" :to="{ name: 'catalog-products' }">Batal</VBtn>
        <VBtn :loading="loading || uploadingImage" @click="submit">Simpan Produk</VBtn>
      </VCol>
    </VRow>

    <VSnackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.image-upload-area {
  min-height: 160px;
  background: rgba(var(--v-theme-on-surface), 0.04);
  cursor: pointer;
  transition: background 0.2s;
}
.image-upload-area:hover {
  background: rgba(var(--v-theme-primary), 0.08);
}
.border-dashed {
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
